import { useTranslation } from 'react-i18next';
import { MultiSelectButtonGroup } from 'qampo-ui-library';
import { InputFieldNumber } from '../../UI';
import { DateField } from '../../UI/base';
import type { RecurrenceDaysOfWeekEnum } from '../../../api/generated';
import { WEEKDAYS_IN_ORDER } from './bookingConstants';
import styles from './BookingRecurrenceFields.module.css';

export interface RecurrenceFieldErrors {
  days?: string;
  interval?: string;
  endDate?: string;
}

interface BookingRecurrenceFieldsProps {
  selectedDays: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  endDate: string;
  errors: RecurrenceFieldErrors;
  firstOccurrence: string | null;
  onDaysChange: (days: RecurrenceDaysOfWeekEnum[]) => void;
  onIntervalChange: (weeks: number) => void;
  onEndDateChange: (endDate: string) => void;
}

export const BookingRecurrenceFields = ({
  selectedDays,
  intervalWeeks,
  endDate,
  errors,
  firstOccurrence,
  onDaysChange,
  onIntervalChange,
  onEndDateChange
}: BookingRecurrenceFieldsProps) => {
  const { t } = useTranslation();

  const dayOptions = WEEKDAYS_IN_ORDER.map((option, index) => ({
    id: index,
    value: option.day,
    label: t(option.shortLabelKey)
  }));
  const selectedOptions = dayOptions.filter((option) => selectedDays.includes(option.value));

  return (
    <div className={styles.recurrenceFields}>
      <div>
        <MultiSelectButtonGroup
          label={t('booking-days-label')}
          options={dayOptions}
          value={selectedOptions}
          onChange={(selected) =>
            onDaysChange(
              WEEKDAYS_IN_ORDER.map((option) => option.day).filter((day) =>
                selected.some((item) => item.value === day)
              )
            )
          }
        />
        {errors.days && <span className={styles.fieldError}>{errors.days}</span>}
      </div>

      <div className={styles.intervalRow}>
        <InputFieldNumber
          label={t('booking-interval-label')}
          width="140px"
          value={intervalWeeks}
          allowNegative={false}
          suffixLabel={t('booking-interval-suffix')}
          error={Boolean(errors.interval)}
          helperText={errors.interval}
          onChange={(e) => onIntervalChange(Number(e.target.value) || 0)}
        />
        <DateField
          label={t('booking-end-date-label')}
          value={endDate}
          error={Boolean(errors.endDate)}
          helperText={errors.endDate ?? t('booking-end-date-hint')}
          onChange={onEndDateChange}
        />
      </div>

      {firstOccurrence && (
        <span className={styles.firstOccurrence}>
          {t('booking-first-occurrence', { date: firstOccurrence })}
        </span>
      )}
    </div>
  );
};
